package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.h2Medium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

/**
 * Figma 10046:345558 — popup shown when an unauthenticated visitor hits a
 * gated interaction (join event, create event, tap/swipe a player card,
 * tap a feed post, open a story, like/comment, etc.).
 */
@Composable
fun AccountRequiredModal(
    isOpen: Boolean,
    onClose: () -> Unit,
    onSignedIn: () -> Unit = {},
) {
    if (!isOpen) return

    // Phones get the mobile sign-in card (same surface as the Profile/Chat
    // guest view) instead of the desktop "Account Required" illustration modal.
    if (isMobile()) {
        MobileAccountRequiredPopup(onClose = onClose, onSignedIn = onSignedIn)
        return
    }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(AppColors.Dark.Overlay)
            .onClick { onClose() }
            .animation(
                ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(520)
                .fillMaxWidth()
                .sMargin(16)
                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x09, 0x14, 0x2D))
                .sBorderRadius(22)
                .sPaddingTop(12)
                .sPaddingBottom(40)
                .sPaddingLeft(12)
                .sPaddingRight(12)
                .sGap(22)
                .onClick { it.stopPropagation() }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("box-shadow", "0 24px 48px rgba(0, 0, 0, 0.3)")
                    property("align-items", "center")
                },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                CloseButton(onClose = onClose)
            }

            AccountIllustration()

            Column(
                modifier = Modifier.sGap(32).styleModifier { property("align-items", "center") }
            ) {
                Column(
                    modifier = Modifier
                        .sGap(12)
                        .sPadding(0, 32)
                        .styleModifier {
                            property("align-items", "center")
                            property("max-width", s(432))
                        }
                ) {
                    val t = LocalAppStrings.current
                    SpanText(
                        t.accountRequiredTitle,
                        modifier = Modifier
                            .h2Medium()
                            .color(AppColors.Text.Secondary)
                            .textAlign(TextAlign.Center)
                    )
                    SpanText(
                        t.accountRequiredDescription,
                        modifier = Modifier
                            .bodyLRegular()
                            .color(AppColors.Text.Secondary)
                            .textAlign(TextAlign.Center)
                            .styleModifier { property("line-height", "1.5") }
                    )
                }

                Row(
                    modifier = Modifier.sGap(17),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    com.arena_web.ui.components.auth.GoogleSignInButton(onLoggedIn = onSignedIn)
                    com.arena_web.ui.components.auth.AppleSignInButton(onLoggedIn = onSignedIn)
                }
            }

            TermsFooter()
        }
    }
}

// Mobile counterpart of the desktop modal — a centred popup wrapping the shared
// [GuestWelcomeCard] so the gated-action sign-in prompt matches the Profile/Chat
// guest view. Tap the scrim to dismiss.
@Composable
private fun MobileAccountRequiredPopup(
    onClose: () -> Unit,
    onSignedIn: () -> Unit,
) {
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            // Match the BottomSheet scrim.
            .styleModifier { property("background", "rgba(2, 4, 11, 0.7)") }
            .onClick { onClose() }
            .animation(
                ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .mMaxWidth(390)
                .mPaddingLeft(16).mPaddingRight(16)
                .onClick { it.stopPropagation() }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                ),
            contentAlignment = Alignment.Center
        ) {
            com.arena_web.ui.sections.auth.GuestWelcomeCard(
                onSignedIn = onSignedIn,
                // Solid sheet-coloured fill so it reads as an opaque popup.
                fillColor = "#09142d",
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("box-shadow", "0 24px 48px rgba(0, 0, 0, 0.3)") }
            )
        }
    }
}

@Composable
private fun CloseButton(onClose: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(36)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick { onClose() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            "×",
            modifier = Modifier
                .sFontSize(20)
                .color(AppColors.Text.Secondary)
                .fontWeight(FontWeight.Medium)
        )
    }
}

@Composable
private fun AccountIllustration() {
    // Figma 10258:160507 — person silhouette + 52×54 ellipse "punch-through"
    // disc anchored to the bottom-right + enter arrow centred on top of it.
    //
    // Person + arrow are recoloured to #1A2E5E via CSS mask (the source SVGs
    // are shared with other screens that use their default colours, so we
    // can't edit the assets in place). The ellipse uses the popup surface
    // colour so it visually masks the shoulder of the person under the arrow.
    Box(
        modifier = Modifier
            .sSize(115)
            .position(Position.Relative),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sSize(115)
                .styleModifier {
                    property("background-color", ICON_TINT)
                    property("-webkit-mask", "url('${AppIcons.Profile}') center/contain no-repeat")
                    property("mask", "url('${AppIcons.Profile}') center/contain no-repeat")
                }
        )
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sBottom(-10)
                .sRight(0)
                .sWidth(52)
                .sHeight(54)
                .borderRadius(50.percent)
                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x09, 0x14, 0x2D)),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .sSize(43)
                    .styleModifier {
                        property("background-color", ICON_TINT)
                        property("-webkit-mask", "url('${AppIcons.Enter}') center/contain no-repeat")
                        property("mask", "url('${AppIcons.Enter}') center/contain no-repeat")
                    }
            )
        }
    }
}

private const val ICON_TINT = "#1A2E5E"

@Composable
private fun TermsFooter() {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    Row(
        modifier = Modifier.sGap(4),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            t.accountRequiredTermsPrefix,
            modifier = Modifier.bodyMRegular().color(AppColors.Text.OnSurfaceMuted)
        )
        SpanText(
            t.accountRequiredTerms,
            modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                .cursor(Cursor.Pointer)
                .onClick { ctx.router.navigateTo("/terms") }
        )
        SpanText(" & ", modifier = Modifier.bodyMRegular().color(AppColors.Text.OnSurfaceMuted))
        SpanText(
            t.accountRequiredPrivacy,
            modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                .cursor(Cursor.Pointer)
                .onClick { ctx.router.navigateTo("/privacy") }
        )
    }
}
